import { ConfirmDialogComponent } from '@/components/confirm-dialog/confirm-dialog.component';
import { UserHelper } from '@/helpers/user.helper';
import { Paging } from '@/models/Paging';
import { AlertService } from '@/services/alert.service';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { PropertySale, PropertySalePaginatedList, SalesManagementService, SellingStatus } from '@eemo/api-client';
import { Subscription, catchError, map, merge, of, startWith, switchMap } from 'rxjs';
import { DateFormatPipe } from '../../../extensions/datePipe';
import { TranslateModule } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgIf, NgClass, CurrencyPipe } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SelectComponent } from '../../../components/ui-forms/select/select.component';

@Component({
    selector: 'app-admin-property-sales',
    templateUrl: './admin-property-sales.component.html',
    styleUrls: ['./admin-property-sales.component.scss'],
    standalone: true,
    imports: [SelectComponent, FormsModule, ReactiveFormsModule, MatSlideToggleModule, MatDividerModule, NgIf, MatProgressSpinnerModule, MatTableModule, MatSortModule, MatButtonModule, RouterLink, MatIconModule, MatMenuModule, NgClass, MatPaginatorModule, CurrencyPipe, TranslateModule, DateFormatPipe]
})
export class AdminPropertySalesComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataList: PropertySalePaginatedList;
  dataSource: MatTableDataSource<PropertySale>;

  displayFullColumns = false;
  lightColumns = [
    'propertyName',
    'price',
    'status',
    'dateOnSale',
    'dateSigning',
    'sellerRealtor',
    'buyerRealtor',
    'actions'
  ];
  fullColumns = [
    'propertyName',
    'price',
    'status',
    'mandateType',
    'dateOnSale',
    'dateAgreement',
    'dateSigning',
    'sellerRealtor',
    'buyerRealtor',
    'sellerNotary',
    'buyerNotary',
    'buyer',
    'actions'
  ];
  displayedColumns = [];

  resultsLength = 0;
  isLoadingResults = true;

  availableStatuses: SellingStatus[];

  sub = new Subscription();
  sortSub = new Subscription();
  deleteSub = new Subscription();

  paging = new Paging();

  form = this.fb.group({
    statuses: [
      [
        SellingStatus.UNDER_OFFER,
        SellingStatus.UNDER_AGREEMENT,
        SellingStatus.BANK_LOAN_ACCEPTED,
        SellingStatus.READY_TO_SIGN
      ]
    ]
  });

  get form_statuses() {
    return this.form.get('statuses') as FormControl<SellingStatus[]>;
  }

  constructor(
    private saleService: SalesManagementService,
    private fb: UntypedFormBuilder,
    private alertService: AlertService,
    private changeDetectorRefs: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.displayedColumns = this.lightColumns;
    this.availableStatuses = Object.keys(SellingStatus) as SellingStatus[];
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.sortSub.unsubscribe();
    this.deleteSub.unsubscribe();
  }

  ngAfterViewInit() {
    // If the user changes the sort order, reset back to the first page.
    this.sortSub = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.loadData();
  }

  loadData() {
    this.sub = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;
          return this.saleService.getSaleList(
            this.form_statuses.value,
            this.paginator.pageIndex * this.paginator.pageSize,
            this.paginator.pageSize
          );
        }),
        map((dataList) => {
          this.dataList = dataList;
          // Flip flag to show that loading has finished.
          this.isLoadingResults = false;
          this.paging.length = dataList.paging.totalItems;
          return dataList.list;
        }),
        catchError((error) => {
          this.isLoadingResults = false;
          return of([]);
        })
      )
      .subscribe((data) => {
        const displayedData = [];
        data.forEach((i) => {
          const row = {
            sale: i,
            uid: i.uid,
            propertyName: i.property.name,
            propertyUid: i.property.uid,
            price: i.price,
            status: 'enum.SellingStatus.' + i.status,
            mandateType: i.mandateType ? 'enum.SellingMandateType.' + i.mandateType : '',
            dateOnSale: i.dateOnSale,
            dateAgreement: i.dateAgreement,
            dateSigning: i.dateSigning,
            sellerRealtor: UserHelper.userSummmaryDisplayName(i.sellerRealtor),
            sellerNotary: UserHelper.userSummmaryDisplayName(i.sellerNotary),
            buyerRealtor: UserHelper.userSummmaryDisplayName(i.buyerRealtor),
            buyerNotary: UserHelper.userSummmaryDisplayName(i.buyerNotary),
            buyer: UserHelper.userSummmaryDisplayName(i.buyer)
          };
          displayedData.push(row);
        });
        this.dataSource = new MatTableDataSource(displayedData);
      });
  }

  updateDataFromFilters() {
    this.paginator.pageIndex = 0;
    this.loadData();
  }

  toggleDisplayColumns() {
    if (this.displayFullColumns) {
      this.displayFullColumns = false;
      this.displayedColumns = this.lightColumns;
    } else {
      this.displayFullColumns = true;
      this.displayedColumns = this.fullColumns;
    }
    this.changeDetectorRefs.detectChanges();
  }

  confirmDelete(sale: PropertySale) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: 'Confirmez-vous la suppression de la vente du bien "' + sale.property.name + '" ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDelete(sale);
        }
      });
    }
  }

  doDelete(sale: PropertySale) {
    this.deleteSub = this.saleService.deleteSale(sale.uid).subscribe((result) => {
      this.alertService.success('Suppression effectuée avec succès');
      this.loadData();
    });
  }
}
